import { useCallback, useMemo, useRef, useState } from 'react';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContextRefreshCatalogSelector } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { useContent } from 'src/contexts/ContentContext';
import { ContentId } from 'src/utils/constants/contentId';
import { PurchaseOrderDetailsInput } from 'src/utils/purchaseUtils';
import {
  CreateEventGroupRequest,
  CreateEventGroupResult,
  EventGroupClient,
} from 'src/WebApiController';

import {
  CreateEventGroupFormState,
  EventGroupTicketGroupDraft,
  SlotCreationStatus,
} from './EventGroup.types';
import { buildTicketGroupItems } from './eventGroupUtils';

export const defaultEventGroupFormState: CreateEventGroupFormState = {
  name: '',
  startDate: null,
  endDate: null,
  venueId: null,
  venueName: null,
  venueLocation: null,
  venueConfigId: null,
  categoryId: null,
  expectedEventCount: 1,
  totalCost: null,
};

type EventGroupErrors = Partial<
  Record<keyof CreateEventGroupFormState, string>
>;

/**
 * Owns the placeholder event group creation flow: the info/ticket-group form
 * state, validation, and the two-phase creation (create the group + slots, then
 * add ticket groups to each slot) with per-slot progress tracking and retry of
 * failed slots.
 *
 * @param onCreated Invoked once every event + ticket group has been created
 *   successfully (including after a retry). Lets the caller dismiss the flow,
 *   e.g. close the PurchaseWizard modal.
 */
export const useEventGroupCreation = (onCreated?: () => void) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const refreshCatalog = useCatalogDataContextRefreshCatalogSelector();
  const requiredMsg = useContent(ContentId.Required);
  const atLeastOneEventMsg = useContent(ContentId.MustBeAtLeastOneEvent);

  const [createPlaceholderEvents, setCreatePlaceholderEvents] = useState(false);
  const [formState, setFormState] = useState<CreateEventGroupFormState>(
    defaultEventGroupFormState
  );
  const [errors, setErrors] = useState<EventGroupErrors>({});
  const [ticketGroups, setTicketGroups] = useState<
    EventGroupTicketGroupDraft[]
  >([]);
  const [slotStatuses, setSlotStatuses] = useState<SlotCreationStatus[]>([]);
  const [isCreating, setIsCreating] = useState(false);

  // Kept in refs so the per-slot loop reads/writes the latest values without
  // racing React state batching mid-loop.
  const slotStatusesRef = useRef<SlotCreationStatus[]>([]);
  const createResultRef = useRef<CreateEventGroupResult | null>(null);
  const purchaseOrderRef = useRef<PurchaseOrderDetailsInput | null>(null);

  // Latest completion callback, read via ref so writeStatuses/create stay
  // referentially stable regardless of the caller's closure churn.
  const onCreatedRef = useRef(onCreated);
  onCreatedRef.current = onCreated;

  const writeStatuses = useCallback(
    (statuses: SlotCreationStatus[]) => {
      slotStatusesRef.current = statuses;
      setSlotStatuses(statuses);

      // Side effect: once every slot has been created, refresh the catalog so
      // the new events are pulled from the server (getCatalogSlim) and replace
      // the placeholders in the tiles/inventory behind the flow, then let the
      // caller dismiss the flow (e.g. close the PurchaseWizard modal).
      if (statuses.length > 0 && statuses.every((s) => s === 'done')) {
        refreshCatalog();
        onCreatedRef.current?.();
      }
    },
    [refreshCatalog]
  );

  const setSlotStatus = useCallback(
    (index: number, status: SlotCreationStatus) => {
      writeStatuses(
        slotStatusesRef.current.map((s, i) => (i === index ? status : s))
      );
    },
    [writeStatuses]
  );

  const onFormChange = useCallback(
    (partial: Partial<CreateEventGroupFormState>) => {
      setFormState((prev) => ({ ...prev, ...partial }));
      // Clear errors for any field the user just edited.
      setErrors((prev) => {
        const cleared = { ...prev };
        (Object.keys(partial) as (keyof CreateEventGroupFormState)[]).forEach(
          (key) => {
            cleared[key] = undefined;
          }
        );
        return cleared;
      });
    },
    []
  );

  const validate = useCallback(() => {
    const newErrors: EventGroupErrors = {};
    if (!formState.name.trim()) newErrors.name = requiredMsg;
    if (formState.venueId == null) newErrors.venueId = requiredMsg;
    if (formState.categoryId == null) newErrors.categoryId = requiredMsg;
    if (formState.expectedEventCount < 1)
      newErrors.expectedEventCount = atLeastOneEventMsg;
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formState, requiredMsg, atLeastOneEventMsg]);

  const buildRequest = useCallback(
    (purchaseOrder: PurchaseOrderDetailsInput): CreateEventGroupRequest => ({
      eventGroupId: window.crypto.randomUUID(),
      name: formState.name,
      venueId: formState.venueId!,
      venueConfigId: formState.venueConfigId,
      categoryId: formState.categoryId!,
      expectedEventCount: formState.expectedEventCount,
      startDate: formState.startDate ?? null,
      endDate: formState.endDate ?? null,
      totalCost: formState.totalCost ?? null,
      // Ticket groups are added per slot below, not on the PO shell.
      purchaseOrder: { ...purchaseOrder, ticketGroups: [] },
    }),
    [formState]
  );

  const buildTicketGroupItemsCb = useCallback(
    () =>
      buildTicketGroupItems(
        ticketGroups,
        formState.expectedEventCount,
        formState.totalCost
      ),
    [formState.expectedEventCount, formState.totalCost, ticketGroups]
  );

  /**
   * Adds ticket groups to each created slot, tracking per-slot status. When
   * retryOnly is true, slots already marked 'done' are skipped.
   *
   * buildTicketGroupItems now returns one array per event, so each slot
   * receives its own set of ticket groups (unitCost may differ by ±$0.01
   * across events to ensure the grand total sums exactly).
   */
  const processSlots = useCallback(
    async (result: CreateEventGroupResult, retryOnly: boolean) => {
      const client = new EventGroupClient(activeAccountWebClientConfig);
      const itemsPerEvent = buildTicketGroupItemsCb();

      for (let i = 0; i < result.slots.length; i++) {
        if (retryOnly && slotStatusesRef.current[i] === 'done') continue;

        setSlotStatus(i, 'in-progress');
        try {
          const items = itemsPerEvent[i] ?? [];
          if (items.length > 0) {
            await client.addEventGroupTicketGroup({
              eventGroupId: result.eventGroupId,
              purchaseOrderId: result.purchaseOrderId,
              posEventId: result.slots[i].posEventId,
              ticketGroups: items,
            });
          }
          setSlotStatus(i, 'done');
        } catch (error) {
          showErrorDialog(
            'useEventGroupCreation.processSlots',
            error as Error,
            {}
          );
          setSlotStatus(i, 'error');
        }
      }
    },
    [
      activeAccountWebClientConfig,
      buildTicketGroupItemsCb,
      setSlotStatus,
      showErrorDialog,
    ]
  );

  /**
   * Phase 1: create the event group (PO + placeholder slots). Phase 2: add the
   * ticket groups to each slot. Errors are surfaced as per-slot 'error' status
   * so the caller can offer a retry rather than throwing.
   */
  const create = useCallback(
    async (purchaseOrder: PurchaseOrderDetailsInput) => {
      if (formState.venueId == null || formState.categoryId == null) return;

      purchaseOrderRef.current = purchaseOrder;
      createResultRef.current = null;
      setIsCreating(true);
      writeStatuses(Array(formState.expectedEventCount).fill('pending'));

      try {
        const client = new EventGroupClient(activeAccountWebClientConfig);
        let result: CreateEventGroupResult;
        try {
          result = await client.createEventGroup(buildRequest(purchaseOrder));
          createResultRef.current = result;
        } catch (error) {
          showErrorDialog('useEventGroupCreation.create', error as Error, {});
          // Nothing was created — mark every slot failed so the whole operation
          // can be retried.
          writeStatuses(Array(formState.expectedEventCount).fill('error'));
          return;
        }

        // Align status count with the slots the server actually created.
        writeStatuses(Array(result.slots.length).fill('pending'));
        await processSlots(result, false);
      } finally {
        setIsCreating(false);
      }
    },
    [
      activeAccountWebClientConfig,
      buildRequest,
      formState.categoryId,
      formState.expectedEventCount,
      formState.venueId,
      processSlots,
      showErrorDialog,
      writeStatuses,
    ]
  );

  /** Re-runs failed work: recreates the group if creation itself failed, then
   * retries only the slots that haven't succeeded. */
  const retry = useCallback(async () => {
    const purchaseOrder = purchaseOrderRef.current;
    if (!purchaseOrder) return;

    setIsCreating(true);
    try {
      let result = createResultRef.current;
      if (!result) {
        const client = new EventGroupClient(activeAccountWebClientConfig);
        try {
          result = await client.createEventGroup(buildRequest(purchaseOrder));
          createResultRef.current = result;
          writeStatuses(Array(result.slots.length).fill('pending'));
        } catch (error) {
          showErrorDialog('useEventGroupCreation.retry', error as Error, {});
          return;
        }
      }
      await processSlots(result, true);
    } finally {
      setIsCreating(false);
    }
  }, [
    activeAccountWebClientConfig,
    buildRequest,
    processSlots,
    showErrorDialog,
    writeStatuses,
  ]);

  const reset = useCallback(() => {
    setCreatePlaceholderEvents(false);
    setFormState(defaultEventGroupFormState);
    setErrors({});
    setTicketGroups([]);
    writeStatuses([]);
    setIsCreating(false);
    createResultRef.current = null;
    purchaseOrderRef.current = null;
  }, [writeStatuses]);

  const hasFailedSlots = slotStatuses.some((s) => s === 'error');
  const allSlotsDone = useMemo(
    () => slotStatuses.length > 0 && slotStatuses.every((s) => s === 'done'),
    [slotStatuses]
  );

  return {
    createPlaceholderEvents,
    setCreatePlaceholderEvents,
    formState,
    errors,
    onFormChange,
    ticketGroups,
    setTicketGroups,
    slotStatuses,
    isCreating,
    hasFailedSlots,
    allSlotsDone,
    validate,
    create,
    retry,
    reset,
  };
};
